import { useCombobox } from 'downshift';
import React, { useRef } from 'react';
// import CleanIcon from '../icons/CleanIcon';

export class OptionElementType {
  value: string;
  title: string;
  description: string;
  constructor(value: string, title: string, description: string) {
    this.value = value;
    this.title = title;
    this.description = description;
  }
}

export type GenericSelectProps = {
  labelText: string;
  initialValue: OptionElementType;
  options: OptionElementType[];
  placeholder?: string;
  custom_test_id?: string;
  onChange?: (value: OptionElementType) => void;
};

function getElementsFilter(inputValue: string) {
  const lowerCasedInputValue = inputValue.toLowerCase();

  return function filter(option: OptionElementType) {
    return (
      !inputValue ||
      option.value.includes(lowerCasedInputValue) ||
      option.title.toLowerCase().includes(lowerCasedInputValue) ||
      option.description.toLowerCase().includes(lowerCasedInputValue)
    );
  };
}
export const GenericSearchableSelect: React.FC<GenericSelectProps> = ({
  options,
  placeholder,
  labelText,
  initialValue,
  onChange,
  custom_test_id,
}) => {
  const [items, setItems] = React.useState(options);
  // Track the current valid selection (either initial value or newly selected item)
  const [currentSelection, setCurrentSelection] =
    React.useState<OptionElementType>(initialValue);
  // Update current selection when initialValue changes
  React.useEffect(() => {
    setCurrentSelection(initialValue);
  }, [initialValue]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const comboboxRef = useRef<any>(null);

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    selectedItem,
    selectItem, // function to select an item programmatically
    highlightedIndex,
    openMenu,
    closeMenu, // function to close the menu programmatically
  } = useCombobox({
    onInputValueChange({ inputValue }) {
      setItems(options.filter(getElementsFilter(inputValue)));
    },
    items,
    itemToString(item) {
      return item ? item.title : '';
    },
    // toggleButtonId: `toggle-button_${labelText}`,
    onSelectedItemChange({ selectedItem }) {
      if (selectedItem) {
        setCurrentSelection(selectedItem);
        onChange?.(selectedItem);
      }
    },
    initialInputValue: initialValue.title,
  });

  // Assign the combobox instance to the ref
  comboboxRef.current = { selectedItem, selectItem };

  function selectCurrentItem(selectedItem: OptionElementType | null) {
    comboboxRef.current.selectItem({
      value: selectedItem?.value ?? '',
      title: selectedItem?.title ?? '',
      description: selectedItem?.description ?? '',
    });
  }

  // Get input props without merging toggle button props to maintain proper tab navigation
  const inputProps = getInputProps();

  const inputRef = useRef<HTMLInputElement>(null);
  const mouseClickedRef = useRef<boolean>(false);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Only select text if focused by mouse click, not by keyboard (Tab)
    if (mouseClickedRef.current) {
      e.target.select();
      mouseClickedRef.current = false;
    }
    // Show all options
    setItems(options);
    openMenu();
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // Mark that this focus was triggered by a click
    mouseClickedRef.current = true;
    // Select all text in the input field on click
    e.currentTarget.select();
    // Show all options
    setItems(options);
    openMenu();
  };

  const handleMouseDown = () => {
    // Set flag before focus event fires
    mouseClickedRef.current = true;
  };

  return (
    <div>
      <div className="w-1/2 flex flex-col gap-1">
        <label className="w-fit label form-control w-48" {...getLabelProps()}>
          <span className="label-text">{labelText}</span>
        </label>
        <div className="relative flex shadow-sm bg-base-100 gap-0.5">
          {/* The searching input box */}
          <input
            ref={inputRef}
            data-testid={custom_test_id}
            placeholder={placeholder ?? 'Search...'}
            className="select select-primary w-full p-1.5"
            {...inputProps}
            id={`input_${labelText}`}
            tabIndex={0}
            onMouseDown={handleMouseDown}
            onFocus={handleFocus}
            onClick={handleClick}
            onBlur={() => {
              // When leaving the input, restore the current valid selection
              selectCurrentItem(currentSelection);
              // Close the menu to prevent overlapping dropdowns
              closeMenu();
            }}
          />
          <ul
            className={`absolute bg-base-100 shadow-md max-h-80 overflow-scroll p-0 z-10 ${
              !(isOpen && items.length) && 'hidden'
            }`}
            style={{
              width: '90%',
              left: '5%',
              top: '100%',
              marginTop: '0.25rem',
            }}
            {...getMenuProps()}
          >
            {isOpen &&
              items.map((item, index) => (
                <li
                  className="py-2 px-3 shadow-sm flex flex-col"
                  key={item.value}
                  style={
                    highlightedIndex === index
                      ? { backgroundColor: 'lightgrey' }
                      : {}
                  }
                  {...getItemProps({ item, index })}
                >
                  <span>{item.title}</span>
                  <span className="text-sm text-base-content">
                    {item.description}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
